import {
    ValidatorConstraint,
    ValidatorConstraintInterface,
    ValidationOptions,
    registerDecorator,
    isPhoneNumber,
    isEmail,
} from 'class-validator';

import container from 'src/container';
import { Creator } from 'src/domain/creator';
import {
    Organization,
    OrganizationGroup,
    OrganizationRole,
} from 'src/domain/organization';

import { NextOfKin } from '../next-of-kin';
import i18n from 'src/i18n';

const { munduClient } = container.cradle;
@ValidatorConstraint({ name: 'usernameExistsConstraint', async: true })
export class UsernameExistsConstraint implements ValidatorConstraintInterface {
    async validate(name: unknown): Promise<boolean> {
        if (typeof name !== 'string') return false;
        if (name.length > 100 || name.length < 3) return false;
        try {
            const exists = await munduClient.getUsernameExists(name);
            if (exists) return false;
            return true;
        } catch (e) {
            return false;
        }
    }
}

export function UsernameExists(validationOptions?: ValidationOptions) {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return (object: Object, propertyName: string): void => {
        registerDecorator({
            name: 'UsernameExists',
            target: object.constructor,
            propertyName: propertyName,
            options: validationOptions,
            validator: UsernameExistsConstraint,
        });
    };
}

@ValidatorConstraint({ name: 'phonenumberExistsConstraint', async: true })
export class PhoneNumberExistConstraint
    implements ValidatorConstraintInterface
{
    async validate(phonenumber: unknown): Promise<boolean> {
        if (typeof phonenumber !== 'string') return false;
        if (!isPhoneNumber(phonenumber, 'NO')) return false;
        try {
            const exists = await munduClient.getPhoneNumberExists(phonenumber);
            if (exists) return false;
            return true;
        } catch (e) {
            return false;
        }
    }
}
export function PhoneNumberExists(validationOptions?: ValidationOptions) {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return (object: Object, propertyName: string): void => {
        registerDecorator({
            name: 'PhoneNumberExists',
            target: object.constructor,
            propertyName: propertyName,
            options: validationOptions,
            validator: PhoneNumberExistConstraint,
        });
    };
}

@ValidatorConstraint({ name: 'emailExistConstraint', async: true })
export class EmailExistConstraint implements ValidatorConstraintInterface {
    async validate(email: unknown): Promise<boolean> {
        if (typeof email !== 'string') return false;
        if (!isEmail(email)) return false;
        try {
            const exists = await munduClient.getEmailExists(email);
            if (exists) return false;
            return true;
        } catch (e) {
            return false;
        }
    }
}
export function EmailExist(validationOptions?: ValidationOptions) {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return (object: Object, propertyName: string): void => {
        registerDecorator({
            name: 'EmailExist',
            target: object.constructor,
            propertyName: propertyName,
            options: validationOptions,
            validator: EmailExistConstraint,
        });
    };
}
export const checkIfEmailExist = async (email: string) => {
    const exists = await munduClient.getEmailExists(email);
    if (exists) throw new Error(i18n.t('errors.emailInUse'));
};

export const checkIfPhoneNumberExist = async (phoneNumber: string) => {
    const exists = await munduClient.getPhoneNumberExists(phoneNumber);
    if (exists) throw new Error(i18n.t('errors.phoneInUse'));
};

export const checkIfUsernameExist = async (username: string) => {
    const exists = await munduClient.getUsernameExists(username);
    if (exists) throw new Error(i18n.t('errors.usernameInUse'));
};

export enum SASName {
    avatarSAS = 'avatarSAS',
    commonMediaSAS = 'commonMediaSAS',
    personalMediaSAS = 'personalMediaSAS',
    organizationDocumentsSAS = 'organizationDocumentsSAS',
}

export type User = Creator | NextOfKin | Organization;

export enum UserRole {
    consumer = 'consumer',
    creator = 'creator',
    organization = 'organization',
    organizationB2C = 'organizationB2C',
    nextOfKin = 'next-of-kin',
}

export interface UserMembership {
    createdDate: string;
    organizationId: string;
    role: UserRole;
    followers?: {
        creatorIds?: string[];
        consumerIds?: string[];
        nextOfKinIds?: string[];
    };
    roles?: OrganizationRole[];
    groups?: OrganizationGroup[];
    groupIds?: string[];
    roleIds?: string[];
    organizationAdmin?: boolean;
}

export interface SASInfo {
    value: string;
    expiresOn: string;
    containerName: string;
}

export interface UserInfo {
    id: string;
    createdDate: string;
    updatedDate?: string;
    lastLoggedInAt?: string;
    hasEverLoggedIn?: boolean;
    profile: {
        firstName: string;
        lastName: string;
        avatar?: string;
    };
    memberships: UserMembership[];
    role?: UserRole;
    isAdmin?: boolean;
    adminPermissions?: string[];
    supportMode?: {
        organizationId: string;
        organizationName: string;
        expiresAt: string;
        readOnly: true;
    };
}

export const getUserRoleLabel = (role?: UserRole | string) => {
    switch (role) {
        case UserRole.organization:
        case UserRole.organizationB2C:
            return i18n.t('roles.organization');
        case UserRole.creator:
            return i18n.t('roles.creator');
        case UserRole.nextOfKin:
            return i18n.t('roles.nextOfKin');
        case UserRole.consumer:
            return i18n.t('roles.consumer');
        default:
            return i18n.t('roles.unknown');
    }
};

export const getPrimaryMembership = (
    user?: Pick<UserInfo, 'memberships'>,
): UserMembership | undefined => {
    return user?.memberships?.[0];
};

export const getPrimaryRole = (
    user?: Pick<UserInfo, 'role' | 'memberships'>,
): UserRole | undefined => {
    return user?.role ?? getPrimaryMembership(user)?.role;
};

export const isOrganizationRole = (role?: UserRole) => {
    return role === UserRole.organization || role === UserRole.organizationB2C;
};

export const isOrganizationAdministrator = (
    user?: Pick<UserInfo, 'id' | 'isAdmin' | 'role' | 'memberships'>,
    organizationId?: string,
): boolean => {
    if (!user) return false;
    if (user.isAdmin) return true;

    if (
        isOrganizationRole(user.role) &&
        (!organizationId || user.id === organizationId)
    ) {
        return true;
    }

    const memberships = user.memberships ?? [];
    const relevantMemberships = organizationId
        ? memberships.filter(
              membership => membership.organizationId === organizationId,
          )
        : memberships.slice(0, 1);

    return relevantMemberships.some(
        membership =>
            membership.organizationAdmin === true ||
            isOrganizationRole(membership.role),
    );
};

export const roleMatches = (
    role: UserRole | undefined,
    required: UserRole,
): boolean => {
    if (!role) return false;
    if (isOrganizationRole(role) && isOrganizationRole(required)) {
        return true;
    }
    return role === required;
};

export const getPrimaryFollowers = (
    user?: Pick<UserInfo, 'memberships'>,
): UserMembership['followers'] | undefined => {
    return getPrimaryMembership(user)?.followers;
};

export const isUserAdmin = (user?: Pick<UserInfo, 'isAdmin'>) => {
    return Boolean(user?.isAdmin);
};
export const getFullName = (user: Pick<User, 'profile'>): string => {
    return `${user.profile.firstName} ${user.profile.lastName ?? ''}`.trim();
};

export const getNameInitials = (user: Pick<User, 'profile'>): string => {
    return `${user.profile.firstName
        .slice(0, 1)
        .toUpperCase()}${user.profile.lastName.slice(0, 1).toUpperCase()}`;
};

const isOrganization = (user: User): user is Organization => {
    const role = getPrimaryRole(user);
    return isOrganizationRole(role);
};

export const getOrganizationId = (user: User): string => {
    const membershipOrganizationId = getPrimaryMembership(user)?.organizationId;
    if (membershipOrganizationId) {
        return membershipOrganizationId;
    }

    if (isOrganization(user)) {
        return user.id;
    }

    return user.organizationId;
};
